import type { CustomerAddressItem } from "~/types/ecom/address.type";
import type { TrolleyResponse } from "~/types/ecom/trolley/trolley.type";
import type { GuestItem, ResetPasswordItem, User } from "~/types/ecom/user.type";
import data from '~/json/pro_card_labels.json';

const ENDPOINTS = {
  CUSTOMERS: (customerId: string) => `/customers/${customerId}`,
  CUSTOMER_ORDERS: (customerId: string) => `/customers/${customerId}/orders`,
  CUSTOMER_PURCHASE_HISTORY: (customerId: string) =>
    `/customers/${customerId}/purchase-history`,
  CUSTOMER_PASSWORD: (customerId: string) =>
    `/customers/${customerId}/password`,
  CUSTOMER_ACCOUNTS: (customerId: string) =>
    `/customers/${customerId}/accounts`,
  CUSTOMER_TROLLEYS: (customerId: string) =>
    `/customers/${customerId}/trolleys`,
  CUSTOMER_ACTIVE_TROLLEYS: (customerId: string) =>
    `/customers/${customerId}/trolleys/active`,
  CUSTOMER_CONTACT_PREFERENCES: (customerId: string) =>
    `/customers/${customerId}/contact-preferences`,
  CUSTOMER_CATALOGUE_REQUESTS: (customerId: string) =>
    `/customers/${customerId}/catalogue-requests`,
  CUSTOMERS_LAST_CATALOGUE_REQUEST: (customerId: string) =>
    `/customers/${customerId}/last-catalogue-request`,
  CUSTOMER_SAVED_LISTS: (customerId: string) =>
    `/customers/${customerId}/saved-lists`,
  FROM_TROLLEY_ID: (trolleyId: string) =>
    `/saved-lists/create-from-trolley/${trolleyId}`,
  CUSTOMER_CARD_REQUEST: (customerId: string) =>
    `/customers/${customerId}/card-request`,
  CUSTOMER_FAVOURITE_BRANCHES: (customerId: string) =>
    `/customers/${customerId}/favourite-branches`,
  CUSTOMER_FAVOURITE_BRANCH: (customerId: string, branchId: string) =>
    `/customers/${customerId}/favourite-branches/${branchId}`,
  CUSTOMER_DEFAULT_BRANCH: (customerId: string) =>
    `/customers/${customerId}/default-branch`,
  CUSTOMER_USERNAME_CHECK: () => `/customers/username-check`,
  CUSTOMER_REQUEST_ACCOUNT_DELETION: (customerId: string) =>
    `/customers/${customerId}/request-account-deletion`,

  // Promotions
  CUSTOMER_APPLY_PROMOTION: () => `/customers/promotion`,
  CUSTOMER_PROMOTIONS: (customerId: string) =>
    `/customers/${customerId}/promotions`,

  // Address book
  CUSTOMER_ADDRESS_BOOK: (customerId: string) =>
    `/customers/address-book/${customerId}`,
  CUSTOMER_ADDRESS_BOOK_PRIMARY_ADDRESS: (customerId: string) =>
    `/customers/address-book/${customerId}/primary-address`,
  CUSTOMER_ADDRESS_BOOK_ADDRESS: (addressId: string) =>
    `/customers/address-book/${addressId}`,

  // Auth
  CUSTOMER_AUTH_LOGIN: `/customers/auth/login`,
  CUSTOMER_AUTH_LOGOUT: `/customers/auth/logout`,
  CUSTOMER_AUTH_REGISTER: `/customers/auth/register`,
  CUSTOMER_AUTH_GUEST_REGISTER: `/customers/auth/guest/register`,
  CUSTOMER_AUTH_RESET_PASSWORD: `/customers/auth/password/reset`,
  CUSTOMER_AUTH_RESET_PASSWORD_EMAIL: `/customers/auth/password/email`,
  CUSTOMER_AUTH_CONVERT_GUEST_TO_CUSTOMER: (customerId: string) =>
    `/customers/auth/guest/${customerId}/convert-to-customer`,

  CUSTOMER_LOYALTY_CLUB: (customerId: string) =>
    `/customers/${customerId}/loyalty-club`,

  CUSTOMER_LOYALTY_CLUB_LINK: (customerId: string) =>
    `/customers/${customerId}/loyalty-club/rewards-platform-sso-url`,
};

export default class EcomCustomerService {
  static async getCustomerById(
    customer_id: string,
    show_notification = true,
    show_loading_indicator = true,
    headers = {},
    nuxtApp = null,
    runtimeConfig = null
  ) {
    let options = {
      show_notification,
      show_loading_indicator,
      headers,
    };
    try {
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom<{data: User}>(ENDPOINTS.CUSTOMERS(customer_id), options,nuxtApp,runtimeConfig);
    } catch (error) {
      let errorMeta = {
        customerId: customer_id,
        accountDatalayerError: true,
      };
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        "critical",
        "Customer Service Error - trying to fetch customer using getCustomerById()",
        errorMeta
      );
      throw error;
    }
  }

  static async fetchActiveCustomerTrolleys(
    customer_id: string,
    customer_token: string,
    show_notification = true
  ): Promise<{ data: TrolleyResponse[] }> {
    const headers: any = {
      "X-Toolstation-Customer-Id": customer_token,
    };
    let options = {
      headers,
      show_notification,
    };
    const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
    //@ts-ignore
    return await useAjaxEcom(
      ENDPOINTS.CUSTOMER_ACTIVE_TROLLEYS(customer_id),
      options
    );
  }

  static async getAddresses(customer_id: string, query?: object) {
    try {
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(ENDPOINTS.CUSTOMER_ADDRESS_BOOK(customer_id), {
        query,
      });
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        customerId: customer_id,
        query: query,
        accountDatalayerError: true,
      };

      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to fetch saved addresses using getAddresses()",
        errorMeta
      );

      throw error;
    }
  }

  static async redirectToTrackOrder(orderId: string) {
    try{
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(`/orders/${orderId}/tracking-links`);
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        orderId,
      };

      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to track order using redirectToTrackOrder()",
        errorMeta
      );

      throw error;
    }
  }

  static async addAddress(customer_id: string, query?: object) {
    const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
    return await useAjaxEcom(ENDPOINTS.CUSTOMER_ADDRESS_BOOK(customer_id), {
      query,
    });
  }

  static async updateAddress(
    address_id: string,
    new_address: CustomerAddressItem
  ) {
    try {
      const requestOptions: any = {
        method: "put",
        params: new_address,
        show_notifications: false,
      };
  
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(
        ENDPOINTS.CUSTOMER_ADDRESS_BOOK_ADDRESS(address_id),
        requestOptions
      );
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        addressId: address_id,
        accountDatalayerError: true,
      };

      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to update address using updateAddress()",
        errorMeta
      );

      throw error;
    }
  }

  static async getProCardOrderHistoryData(data: {
    customerId: string;
    payment_type: string;
  }) {
    const { customerId, payment_type } = data;

    const requestOptions: AjaxEcomOptions = {
      method: "get",
      query: {
        payment_type,
      },
    };
    try {
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(
        `/customers/${customerId}/orders`,
        requestOptions
      );
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        customerId: customerId,
        payment_type: payment_type,
      };

      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to get ProCard Order History Data using getProCardOrderHistoryData()",
        errorMeta
      );

      throw error;
    }
  }

  static async deleteAddress(address_id: string, query?: object) {
    try {
      let options: object = {
        method: "delete",
        query,
      };
  
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(
        ENDPOINTS.CUSTOMER_ADDRESS_BOOK_ADDRESS(address_id),
        options
      );
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        addressId: address_id,
        query,
        accountDatalayerError: true,
      };

      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to delete customer address using deleteAddress()",
        errorMeta
      );
      throw error;
    }
  }

  static async setAddressAsPrimary(customer_id: string, address_id: string) {
    try {
      const options: AjaxEcomOptions = {
        method: "put",
        params: {
          address_id: address_id,
        },
      };
  
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(
        ENDPOINTS.CUSTOMER_ADDRESS_BOOK_PRIMARY_ADDRESS(customer_id),
        options
      );
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        customerId: customer_id,
        addressId: address_id,
        accountDatalayerError: true,
      };

      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to set address as primary using setAddressAsPrimary()",
        errorMeta
      );

      throw error;
    }
  }

  static async getOrders(
    customer_id: string,
    page: number = 1,
    page_limit: number = 50,
    q: string | null = null,
    payment_type: string | null = null
  ) {
    try {
      let options = {
        query: {
          q: q,
          page: page,
          pageLimit: page_limit,
          paymentType: payment_type,
        },
      };
  
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(ENDPOINTS.CUSTOMER_ORDERS(customer_id), options);
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        customerId: customer_id,
        query: { q, page, pageLimit: page_limit, paymentType: payment_type },
        accountDatalayerError: true,
      };

      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to fetch Orders using getOrders()",
        errorMeta
      );

      throw error;
    }
  }

  static async signIn(username: string, password: string) {
    try {
      const options: AjaxEcomOptions = {
        method: "post",
        params: {
          username,
          password,
        },
      };

    const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
    return await useAjaxEcom<{ data: User }>(ENDPOINTS.CUSTOMER_AUTH_LOGIN, options);
    } catch (error) {
      let errorMeta = {
        username,
        password,
        accountDatalayerError: true,
      };
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to signIn using signIn()",
        errorMeta
      );
      throw error;
    }
  }

  static async usernameCheck(username: string) {
    try {
      const options: AjaxEcomOptions = {
        method: "post",
        params: {
          username,
        },
      };

      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom<{ data: { exists: boolean } }>(ENDPOINTS.CUSTOMER_USERNAME_CHECK(), options);
    } catch (error) {
      let errorMeta = {
        username,
        accountDatalayerError: true,
      };
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to check username in ECOM using usernameCheck()",
        errorMeta
      );
      throw error;
    }
  }

  static async postGuestSignUp(item: GuestItem, token: string ) {
    try {
      const options: AjaxEcomOptions = {
        method: "post",
        params: item,
        headers: {
          "x-toolstation-session-id": token,
        },
      };

    const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
    return await useAjaxEcom<{ data: User }>(ENDPOINTS.CUSTOMER_AUTH_GUEST_REGISTER, options);
    } catch (error) {
      let errorMeta = {
        item,
        token,
        accountDatalayerError: true,
      };
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to fetch postGuestSignUp using postGuestSignUp()",
        errorMeta
      );
      throw error;
    }
  }

  static async sendForgotPasswordMail(username: string) {
    try {
      const options: AjaxEcomOptions = {
        method: "post",
        params: {
          username,
        },
      };
 
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom<{ data: { msg: string } }>(
        ENDPOINTS.CUSTOMER_AUTH_RESET_PASSWORD_EMAIL,
        options
      );
    } catch (error) {
      let errorMeta = {
        username,
        accountDatalayerError: true,
      };
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to send forgot password mail using sendForgotPasswordMail()",
        errorMeta
      );
      throw error;
    }
  }

  static async resetPassword(item: ResetPasswordItem) {
    try {
      const options: AjaxEcomOptions = {
        method: "post",
        params: item,
      };

    const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
    return await useAjaxEcom<{ data: string }>(ENDPOINTS.CUSTOMER_AUTH_RESET_PASSWORD, options);
    } catch (error) {
      let errorMeta = {
        item,
        accountDatalayerError: true,
      };
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to reset customer password using resetPassword()",
        errorMeta
      );
      throw error;
    }
  }

  static async handleCreditIncreaseUserDetails(data: object) {
    const options: AjaxEcomOptions = {
      method: "post",
      params: data,
    };
    try {
      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(`/procard/increase-limit`, options);
    } catch (error) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = {
        data: data,
      };

      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to increase credit limit using handleCreditIncreaseUserDetails()",
        errorMeta
      );

      throw error;
    }
  }

  static async signUp(sign_up_form: any) {
    try {
      const options: AjaxEcomOptions = {
        method: "post",
        params: sign_up_form,
      };
    const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
    return await useAjaxEcom(ENDPOINTS.CUSTOMER_AUTH_REGISTER, options);
    } catch (error) {
      let errorMeta = {
        sign_up_form,
        accountDatalayerError: true,
      };
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to signUp using signUp()",
        errorMeta
      );
      throw error;
    }
  }

  static async setDefaultBranch(customer_id: string, site_id: string) {
    try {
      let options: AjaxEcomOptions = {
        method: "put",
        params: {
          site_id: site_id,
        },
      };

      const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
      return await useAjaxEcom(
        ENDPOINTS.CUSTOMER_DEFAULT_BRANCH(customer_id),
        options
      );
    } catch (err) {
      const { $customClientErrorHandler } = useNuxtApp();
      let errorMeta = { customerError: true };

      $customClientErrorHandler(
        err,
        "critical",
        "Default Branch Error - trying to set default branch using setDefaultBranch()",
        errorMeta
      );
      throw err;
    }
  }

  static async fetchCustomerDetails(customerId: string) {
    const options: AjaxEcomOptions = {
      method: "get",
    };

    const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
    return await useAjaxEcom(
      `/customers/${customerId}`,
      options
    );
  }

  static async getOneTimeQrCodeUserDetails(customerId: string) {
    try {
      const options: AjaxEcomOptions = {
        method: "post",
      };

    const { useAjaxEcom } = await import("~/composables/useAjaxEcom");
    return await useAjaxEcom<{ data: User }>(
      `/customers/${customerId}/one-time-qrcode`,
      options
    );
    
    } catch (error) {
      let errorMeta = {
        customerId,
        accountDatalayerError: true,
      };
      const { $customClientErrorHandler } = useNuxtApp();
      $customClientErrorHandler(
        error,
        "critical",
        "CustomerServiceError - trying to user specific qr code using getOneTimeQrCodeUserDetails()",
        errorMeta
      );
      throw error;
    }
  }
}
